import { Node, useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import classNames from "classnames";

import { faTrash } from "@fortawesome/pro-regular-svg-icons";

import { useParams } from "react-router-dom";

import { InputField } from "@components/InputField/InputField";

import {
	createDefaultPhaseData,
	PhaseData,
} from "@/pages/Private/pages/Courses/components/Editor/NodeDataTyps";
import { DecisionConfirmationTypes } from "@/pages/Private/pages/Course/enums/DecisionConfirmationTypes.enum";
import { FileTypes } from "@/pages/Private/pages/Courses/enums/FileTypes.enum";
import { useCourseFiles } from "@/pages/Private/helpers/useCourseFiles";
import { FileAssignment } from "@/pages/Private/pages/Courses/enums/FileAssignment.enum";
import { PhaseEndResult } from "@/pages/Private/pages/Courses/enums/PhaseEndResult.enum";

import { Icon, Loader, Modal } from "@/components";

import { Toggle } from "@/components/Toggle/Toggle";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { Button } from "@/components/Button/Button";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { TextArea } from "@/components/TextArea/TextArea";

import { useGenerateTtsMutation } from "@/pages/Private/redux/courses/courses.api";
import { phaseMediaUrl } from "@/utils/phaseMedia";

import { MediaPickerModal } from "../MediaLibrary/MediaPickerModal";
import { AiImagePanel } from "./AiImagePanel";
import { getNextAvailablePhaseDataId, getNextAvailableNodeId } from "./graphUtils";
import { dropStaleFallback } from "./fallback";
import VoiceSelectModal from "./VoiceSelectModal";
import CollapsibleSection from "./CollapsibleSection";
import { PhaseEffect } from "../../enums/PhaseEffect.enum";
import { AvatarButtons } from "../../../Course/components/AvatarButtons";

type DecisionOption = {
	option: string;
	phaseId: number | null; // null ist jetzt erlaubt
};

type TimeDelayedContentItem = NonNullable<PhaseData["timeDelayedContent"]>[0];

type PhaseEditorProps = {
	node: Node<PhaseData>;
	handlePaneClick?: () => void;
	handleDeletePhase: () => void;
	courseLanguage?: string;
};
export default function PhaseEditor({ node, handleDeletePhase, courseLanguage }: PhaseEditorProps) {
	// 1. useCourseFiles Hook für Datei-Upload und Löschung
	const { id } = useParams();
	const { uploadCourseFiles, isLoading: isUploadingFiles, deleteCourseFile } = useCourseFiles();

	const videoRef = useRef<HTMLVideoElement>(null);
	const audioRef = useRef<HTMLAudioElement>(null);
	const videoDelayedContentRef = useRef<HTMLVideoElement>(null);
	const audioDelayedContentRef = useRef<HTMLAudioElement>(null);

	// 2. React Flow SetNodes Hook
	const { setNodes, getNodes, getEdges } = useReactFlow();
	const [showDecisionModal, setShowDecisionModal] = useState(false);
	const [showSpoilerModal, setShowSpoilerModal] = useState(false);
	const [showTimeDelayedModal, setShowTimeDelayedModal] = useState(false);
	const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

	// 3. Lokaler State für Formulardaten (PhaseData)
	const [formData, setFormData] = useState<PhaseData>(node.data);

	// How the author supplies this phase's content: an upload, or a generation. "tts" is
	// offered for AUDIO, "ai" for IMAGE; every other content type is upload-only.
	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const [contentInputMode, setContentInputMode] = useState<"upload" | "tts" | "ai">("upload");
	const [ttsText, setTtsText] = useState("");
	const [selectedVoiceId, setSelectedVoiceId] = useState<string>("");
	const [selectedVoiceName, setSelectedVoiceName] = useState<string>("");
	const [showVoiceModal, setShowVoiceModal] = useState(false);
	const [ttsError, setTtsError] = useState<string>("");
	const [generateTts, { isLoading: isGeneratingTts }] = useGenerateTtsMutation();

	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`courses.${key}`), [t]);
	const te = useCallback((key: string) => t(`editor.${key}`), [t]);
	const decisionsSectionRef = useRef<HTMLDivElement | null>(null);
	const spoilerSectionRef = useRef<HTMLDivElement | null>(null);
	const timeDelayedSectionRef = useRef<HTMLDivElement | null>(null);

	// Which generator, if any, this content type offers alongside the upload.
	const generateMode = useMemo<"tts" | "ai" | null>(() => {
		if (formData.contentType === FileTypes.Audio) {
			return "tts";
		}

		return formData.contentType === FileTypes.Image ? "ai" : null;
	}, [formData.contentType]);

	// Switching content type must not leave the panel on a generator the new type has no
	// concept of.
	useEffect(() => {
		setContentInputMode((current) =>
			current !== "upload" && current !== generateMode ? "upload" : current
		);
	}, [generateMode]);

	// Synchronisiere formData, wenn node Daten oder ID sich ändern
	useEffect(() => {
		setFormData(node.data);
	}, [node.id, node.data]);

	// Imported scenarios store a video as the whole `<video>` tag rather than its URL. The
	// players and the file name want the file, so everything below reads it out once.
	const contentUrl = useMemo(
		() => phaseMediaUrl(formData.content, formData.contentType),
		[formData.content, formData.contentType]
	);

	const currentNodeHasEdges = useMemo(() => {
		const edges = getEdges();

		return edges.some((edge) => edge.source === node.id);
	}, [getEdges, node.id]);

	const nextNodes = useMemo(() => {
		const nodes = getNodes();
		const edges = getEdges();

		const edgesToNextNodes = edges.filter((edge) => edge.source === node.id);

		return nodes.filter((n) => edgesToNextNodes.some((edge) => edge.target === n.id));
	}, [getEdges, getNodes, node.id]);

	const handleSaveFormState = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		inputValue: any,
		fieldName: keyof PhaseData | "decisionName" | "effects"
	) => {
		// Neue Daten vorbereiten
		const updatedFormData: PhaseData = { ...formData };

		// console.log(updatedFormData, "handle save form state updatedform data");
		// if (fieldName === "json") {
		// 	const nodes = getNodes();

		// 	updatedFormData.json = nodes.map((node) => {
		// 		return node.data;
		// 	});
		// }

		if (fieldName === "decisionName") {
			updatedFormData.decisionName = inputValue as string | undefined;
			const newDecisionNameValue = inputValue as string;

			if (newDecisionNameValue && newDecisionNameValue.trim()) {
				if (!updatedFormData.decisionOptions || updatedFormData.decisionOptions.length === 0) {
					const nextPhaseId =
						typeof updatedFormData.id === "number" && updatedFormData.id >= 0
							? updatedFormData.id + 1
							: 1;

					updatedFormData.decisionOptions = [{ option: ts("newOption"), phaseId: nextPhaseId }];
				}

				if (
					!Object.values(DecisionConfirmationTypes).includes(
						updatedFormData.confirmationRequired as DecisionConfirmationTypes
					)
				) {
					updatedFormData.confirmationRequired = DecisionConfirmationTypes.NONE;
				}
			} else if (!newDecisionNameValue || !newDecisionNameValue.trim()) {
				// Entscheidung ist leer – alles zurücksetzen
				updatedFormData.decisionName = undefined;
				updatedFormData.confirmationRequired = DecisionConfirmationTypes.NONE;
				updatedFormData.decisionOptions = undefined;
			}
		} else {
			// Direktes Feld von PhaseData
			if (
				Object.prototype.hasOwnProperty.call(updatedFormData, fieldName) ||
				[
					"avatar",
					"autoplay",
					"contentType",
					"content",
					"phaseName",
					"timeLimit",
					"timeLeftDecisionId",
					"confirmationRequired",
					"phaseEndText",
					"phaseEndResult",
					"effects",
					"json",
					"requireExplanation",
				].includes(fieldName as string)
			) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(updatedFormData as any)[fieldName as keyof PhaseData] = inputValue;
			}
		}

		// 1. Lokalen Form-Status setzen (für Formular-Anzeige)
		setFormData(updatedFormData);

		// 2. Reaktiven Knoten-Status in der Graph-Struktur aktualisieren
		setNodes((currentNodes) =>
			currentNodes.map((n) =>
				n.id === node.id
					? {
							...n,

							data: updatedFormData,
						}
					: n
			)
		);
	};

	// --- Handlers for Spoiler Content ---
	const addSpoilerContent = () => {
		setFormData((currentFormData) => {
			const newFormData = {
				...currentFormData,
				spoilerContent: { title: "", content: "" },
			};

			setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n)));

			return newFormData;
		});
	};

	// --- TTS Generation Handler ---
	const handleGenerateTts = async () => {
		if (!ttsText.trim() || !selectedVoiceId || !id) {
			return;
		}

		setTtsError("");

		try {
			const result = await generateTts({
				courseId: +id,
				text: ttsText.trim(),
				voiceId: selectedVoiceId,
			}).unwrap();

			const url = result?.fullFilePath;

			if (url?.startsWith("http")) {
				handleSaveFormState(url, "content");

				setTimeout(() => {
					if (audioRef.current) {
						audioRef.current.load();
					}
				}, 200);
			}
		} catch {
			setTtsError(ts("tts.generateError"));
		}
	};

	type CheckboxProps = {
		isChecked: boolean;
		label: string;
		name: string;
		onChange: (checked: boolean) => void;
	};

	const Checkbox = ({ isChecked, label, name, onChange }: CheckboxProps) => (
		<label className="capitalize flex items-center text-[14px] font-bold text-primary-gray">
			{label}
			<input
				checked={isChecked}
				className="form-checkbox h-4 w-4 text-blue-600 ml-2"
				name={name}
				type="checkbox"
				onChange={(e) => onChange(e.target.checked)}
			/>
		</label>
	);

	const removeSpoilerContent = () => {
		setFormData((currentFormData) => {
			const newFormData = { ...currentFormData, spoilerContent: undefined };

			setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n)));

			return newFormData;
		});
	};

	const handleSpoilerContentChange = (subFieldName: "title" | "content", value: string) => {
		setFormData((currentFormData) => {
			const spoiler = currentFormData.spoilerContent || { title: "", content: "" };
			const newSpoilerContent = { ...spoiler, [subFieldName]: value };

			if (!newSpoilerContent.title && !newSpoilerContent.content) {
				const newFormData = { ...currentFormData, spoilerContent: undefined };

				setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n)));

				return newFormData;
			}

			const newFormData = { ...currentFormData, spoilerContent: newSpoilerContent };

			setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n)));

			return newFormData;
		});
	};

	// --- Decision Handling ---
	const handleAddDecisionSection = () => {
		setFormData((currentFormData) => {
			const newFormData: PhaseData = {
				...currentFormData,
				decisionName: currentFormData.decisionName || "",
				decisionOptions: [],
				confirmationRequired:
					currentFormData.confirmationRequired || DecisionConfirmationTypes.NONE,
			};

			setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n)));

			return newFormData;
		});
	};

	const handleRemoveDecisionSection = () => {
		setFormData((currentFormData) => {
			const newFormData: PhaseData = {
				...currentFormData,
				decisionName: undefined,
				decisionOptions: undefined,
				confirmationRequired: undefined,
				timeLimit: undefined,
				timeLeftDecisionId: undefined,
			};

			setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n)));

			return newFormData;
		});
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const updateDecisionOption = (
		index: number,
		key: keyof DecisionOption,
		value: string | number | null
	) => {
		setFormData((currentFormData) => {
			const newOptions = [...(currentFormData.decisionOptions || [])];

			// Sicherstellen, dass die Option existiert
			if (!newOptions[index]) {
				return currentFormData; // Keine Änderung, wenn der Index ungültig ist
			}

			const updatedOption = {
				...newOptions[index],
				[key]: key === "phaseId" ? (value === "" || value === null ? null : Number(value)) : value,
			};

			newOptions[index] = updatedOption;

			const newFormData = dropStaleFallback({ ...currentFormData, decisionOptions: newOptions });

			// Die einzige Aktualisierung: Wir ändern die Daten des aktuellen Knotens.
			// React Flow und unser neuer Effekt kümmern sich um den Rest.
			setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n)));

			return newFormData;
		});
	};

	const addDecisionOptionAndPhase = () => {
		// Wir rufen setFormData mit einer Funktion auf. Das garantiert, dass wir den aktuellsten State erhalten.
		setFormData((currentData) => {
			const currentNodes = getNodes();

			// Wir arbeiten nun mit 'currentData' statt mit der 'node'-Prop.
			const sourceNodeId = currentData.id;

			// Sicherheitsabfrage, falls die ID aus irgendeinem Grund nicht da ist.
			if (sourceNodeId == null) {
				console.error("Source node has no ID, cannot add new option.");

				return currentData;
			}

			const newPhaseDataId = getNextAvailablePhaseDataId(currentNodes);
			const newNodeId = getNextAvailableNodeId(currentNodes);

			const sourceNodeInGraph = currentNodes.find((n) => n.id === sourceNodeId.toString());

			const sourcePosition = sourceNodeInGraph?.position || { x: 100, y: 100 };
			const sourceHeight = sourceNodeInGraph?.height ?? 150;
			const sourceWidth = sourceNodeInGraph?.width ?? 240;

			const horizontalSpacing = 60;
			const minVerticalSpacing = 100; // Der Mindestabstand nach unten

			// --- X-Position (bleibt unverändert) ---
			const existingOptionsCount = Array.isArray(currentData.decisionOptions)
				? currentData.decisionOptions.length
				: 0;
			let xOffset = 0;

			switch (existingOptionsCount) {
				case 0:
					xOffset = -(sourceWidth / 2) - horizontalSpacing;
					break;
				case 1:
					xOffset = sourceWidth / 2 + horizontalSpacing;
					break;
				case 2:
					xOffset = -(sourceWidth / 2) - horizontalSpacing - sourceWidth;
					break;
				case 3:
					xOffset = sourceWidth / 2 + horizontalSpacing + sourceWidth;
					break;
				default:
					xOffset = (existingOptionsCount - 1) * (sourceWidth + horizontalSpacing);
					break;
			}

			const newXPosition = sourcePosition.x + sourceWidth / 2 + xOffset - sourceWidth / 2;

			// --- Y-Position (mit Zufallswert) ---
			// 1. Definiere, wie groß der zusätzliche zufällige Abstand maximal sein darf.
			const maxRandomVerticalOffset = 100; // z.B. bis zu 60px zusätzlicher Abstand

			// 2. Berechne die minimale Y-Position (der bisherige Wert).
			const baseYPosition = sourcePosition.y + sourceHeight + minVerticalSpacing;

			// 3. Addiere einen zufälligen Wert hinzu.
			const newYPosition = baseYPosition + Math.random() * maxRandomVerticalOffset;

			const newNodePosition = {
				x: newXPosition,
				y: newYPosition,
			};

			const newPhaseData = createDefaultPhaseData(t, +id!, {
				id: newPhaseDataId,
				position: newNodePosition,
			});
			const newNode: Node<PhaseData> = {
				id: newNodeId,
				type: "phaseNode",
				position: newNodePosition,
				data: newPhaseData,
			};

			const newDecisionOption = {
				option: "",
				phaseId: newPhaseDataId,
			};

			// Erzeuge die finalen, neuen Daten für den Quell-Knoten
			const updatedSourceNodeData = {
				...currentData,
				decisionOptions: [
					...(Array.isArray(currentData.decisionOptions) ? currentData.decisionOptions : []),
					newDecisionOption,
				],
				decisionName: currentData.decisionName || "",
				confirmationRequired: currentData.confirmationRequired || DecisionConfirmationTypes.NONE,
			};

			// --- STATE UPDATES ---
			setNodes((nds) =>
				nds
					.map((n) =>
						n.id === sourceNodeId.toString() ? { ...n, data: updatedSourceNodeData } : n
					)
					.concat(newNode)
			);

			// Gib die neuen Daten zurück, um den lokalen State des Editors zu aktualisieren
			return updatedSourceNodeData;
		});
	};

	const addDecisionOptionOnly = () => {
		setFormData((currentFormData) => {
			const decisionOptions = [
				...(Array.isArray(currentFormData.decisionOptions) ? currentFormData.decisionOptions : []),
				{ option: "", phaseId: null },
			];

			const newFormData: PhaseData = {
				...currentFormData,
				decisionOptions,
				decisionName: currentFormData.decisionName || "",
				confirmationRequired:
					currentFormData.confirmationRequired || DecisionConfirmationTypes.NONE,
			};

			setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n)));

			return newFormData;
		});
	};

	const removeDecisionOption = (indexToRemove: number) => {
		setFormData((currentFormData) => {
			let newOptions = currentFormData.decisionOptions ? [...currentFormData.decisionOptions] : [];

			newOptions = newOptions.filter((_, index) => index !== indexToRemove);

			const newFormData: PhaseData = dropStaleFallback({
				...currentFormData,
				decisionOptions: newOptions.length > 0 ? newOptions : undefined,
			});

			if (newOptions.length === 0) {
				// If all options are removed, clear the entire decision section
				newFormData.decisionName = undefined;
				newFormData.confirmationRequired = undefined;
				newFormData.timeLimit = undefined;
				newFormData.timeLeftDecisionId = undefined;
			}

			setNodes((nodes) => nodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n)));

			return newFormData;
		});
	};

	// --- Handlers for Time-Delayed Content ---
	const handleTimeDelayedContentChange = (
		index: number,
		itemFieldName: keyof TimeDelayedContentItem,
		value: string | boolean | number
	) => {
		setFormData((currentFormData) => {
			const newTimeDelayedContent = (currentFormData.timeDelayedContent || []).map((item) => ({
				...item,
			}));

			if (!newTimeDelayedContent[index]) {
				console.error(
					"Attempting to update a non-existent timeDelayedContent item at index:",
					index
				);

				return currentFormData;
			}

			const itemToUpdate = { ...newTimeDelayedContent[index] };

			if (itemFieldName === "autoplay") {
				itemToUpdate.autoplay = Boolean(value);
			} else if (itemFieldName === "startTimeInSeconds" || itemFieldName === "endTimeInSeconds") {
				const numValue = parseFloat(value as string);

				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(itemToUpdate as any)[itemFieldName] = isNaN(numValue) ? 0 : numValue;
			} else if (itemFieldName === "contentType") {
				itemToUpdate.contentType = value as FileTypes;
			} else {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(itemToUpdate as any)[itemFieldName] = value as string;
			}

			newTimeDelayedContent[index] = itemToUpdate;
			const newFormData = { ...currentFormData, timeDelayedContent: newTimeDelayedContent };

			setNodes((currentNodes) =>
				currentNodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n))
			);

			return newFormData;
		});
	};

	const addTimeDelayedContentItem = () => {
		setFormData((currentFormData) => {
			const newItem: TimeDelayedContentItem = {
				title: "",
				content: "",
				autoplay: false,
				contentType: FileTypes.Text,
				startTimeInSeconds: null,
				endTimeInSeconds: null,
				large: false,
			};
			const newTimeDelayedContent = [...(currentFormData.timeDelayedContent || []), newItem];
			const newFormData = { ...currentFormData, timeDelayedContent: newTimeDelayedContent };

			setNodes((currentNodes) =>
				currentNodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n))
			);

			return newFormData;
		});
	};

	const removeTimeDelayedContentItem = (indexToRemove: number) => {
		setFormData((currentFormData) => {
			const newTimeDelayedContent = (currentFormData.timeDelayedContent || []).filter(
				(_, index) => index !== indexToRemove
			);
			const newFormData = {
				...currentFormData,
				timeDelayedContent: newTimeDelayedContent.length > 0 ? newTimeDelayedContent : undefined,
			};

			setNodes((currentNodes) =>
				currentNodes.map((n) => (n.id === node.id ? { ...n, data: newFormData } : n))
			);

			return newFormData;
		});
	};

	// --- Animation Effects ---
	useEffect(() => {
		if (formData.spoilerContent && spoilerSectionRef.current) {
			spoilerSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}, [formData.spoilerContent]);

	useEffect(() => {
		if (
			formData.timeDelayedContent &&
			formData.timeDelayedContent.length > 0 &&
			timeDelayedSectionRef.current
		) {
			timeDelayedSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
		// eslint-disable-next-line
	}, [formData.timeDelayedContent?.length]);

	useEffect(() => {
		if (formData.decisionName !== undefined && decisionsSectionRef.current) {
			decisionsSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}, [formData.decisionName]);

	const confirmationRequiredOptions: AutoCompleteItem[] = useMemo(
		() => [
			{
				title: ts("none"),
				id: DecisionConfirmationTypes.NONE,
			},
			{
				title: ts("fromLeader"),
				id: DecisionConfirmationTypes.FROM_LEADER,
			},
			{
				title: ts("fromAll"),
				id: DecisionConfirmationTypes.FROM_ALL,
			},
		],
		[ts]
	);

	const fileTypesOptions: AutoCompleteItem[] = useMemo(
		() => [
			{
				title: ts("text"),
				id: FileTypes.Text,
			},
			{
				title: ts("audio"),
				id: FileTypes.Audio,
			},
			{
				title: ts("image"),
				id: FileTypes.Image,
			},
			{
				title: ts("video"),
				id: FileTypes.Video,
			},
		],
		[ts]
	);

	const currentContentType = useMemo(() => {
		return (
			fileTypesOptions.find((option) => option.id === formData.contentType) ?? fileTypesOptions[0]
		);
	}, [fileTypesOptions, formData.contentType]);

	const [uploadedAvatars, setUploadedAvatars] = useState<{ id: string; url: string }[]>([]);

	const [selectedAvatarId, setSelectedAvatarId] = useState(node?.data?.avatar?.id ?? "");

	const [selectedAvatarUrl, setSelectedAvatarUrl] = useState(node?.data?.avatar?.url ?? "");

	const removeAvatarFile = useCallback(
		async (url: string) => {
			if (url && id) {
				try {
					await deleteCourseFile(url, +id);

					const isUrlInUploadedAvatars = uploadedAvatars.some(
						(file: { id: string; url: string }) => file.url !== node?.data?.avatar?.url
					);

					if (isUrlInUploadedAvatars || selectedAvatarUrl === url) {
						handleSaveFormState({ id: null, url: null }, "avatar");
						setSelectedAvatarUrl("");
						setSelectedAvatarId("");
					}
				} catch (error) {
					console.error("Error deleting file:", error);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedAvatarUrl, id]
	);

	return (
		<div className="flex flex-col justify-between h-full">
			<div className="flex flex-col mb-3">
				<div className="flex flex-col md:flex-row gap-6 w-full">
					<InputField
						containerClassName="flex-grow"
						handleChange={(e) => {
							e.stopPropagation();
							handleSaveFormState(e.target.value, "phaseName");
						}}
						label={ts("phaseName")}
						name={"phaseName"}
						placeholder={ts("phaseName")}
						value={formData.phaseName || ""}
					/>
					<InputField
						containerClassName="sm:w-1/5"
						isDisabled={true}
						label={ts("id")}
						name={"id"}
						value={formData.id.toString()}
					/>
				</div>
				<CollapsibleSection defaultOpen title={te("sectionContent")}>
					<div className="w-full mt-4">
						<label className="capitalize flex items-center text-[14px] font-bold text-primary-gray">
							<Dropdown
								data={fileTypesOptions}
								defaultValue={currentContentType}
								handleSelect={(value) => {
									handleSaveFormState(value?.id as unknown as FileTypes, "contentType");
								}}
								label={ts("contentType")}
							/>
						</label>
					</div>
					<div className="w-full mt-4">
						{formData.contentType === FileTypes.Text && (
							<InputField
								containerClassName="custom-scrollbar"
								handleChange={(e) => {
									e.stopPropagation();
									handleSaveFormState(e.target.value, "content");
								}}
								label={ts("content")}
								multiline={true}
								name="content"
								placeholder={ts("newContent")}
								showError={false}
								value={formData.content || ""}
							/>
						)}

						{formData.contentType !== FileTypes.Text && (
							<div className="space-y-3">
								{/* Upload vs. generate, for the content types that offer a generator */}
								{generateMode && (
									<div className="flex gap-2 mb-3">
										<button
											className={classNames(
												"px-4 py-2 text-sm font-semibold rounded-full transition-colors",
												contentInputMode === "upload"
													? "bg-accent-tint text-text ring-1 ring-accent"
													: "bg-surface text-subtle hover:bg-neutral-100"
											)}
											type="button"
											onClick={() => setContentInputMode("upload")}
										>
											{ts("tts.uploadFile")}
										</button>
										<button
											className={classNames(
												"px-4 py-2 text-sm font-semibold rounded-full transition-colors",
												contentInputMode === generateMode
													? "bg-accent-tint text-text ring-1 ring-accent"
													: "bg-surface text-subtle hover:bg-neutral-100"
											)}
											type="button"
											onClick={() => setContentInputMode(generateMode)}
										>
											{generateMode === "tts"
												? ts("tts.generateFromText")
												: ts("aiImage.generateWithAi")}
										</button>
									</div>
								)}

								{(isUploadingFiles || isGeneratingTts) && (
									<div className="flex justify-center items-center">
										<Loader />
									</div>
								)}

								{/* The library, not a file input. Uploading straight into one phase is how
								    generated and uploaded files became unreachable: the file went in, the
								    phase held its URL, and nothing else could ever find it. Everything now
								    goes through the library, including uploads — the picker has its own
								    upload button and puts what it stores on the shelf as well as on this
								    phase. */}
								{(!generateMode || contentInputMode === "upload") && (
									<div className="mb-2 flex flex-wrap items-center gap-2">
										<Button
											color={ButtonColor.ACTION_SECONDARY}
											disabled={!id}
											size={ButtonSize.S}
											title={ts("mediaLibrary.choose")}
											type="button"
											onClick={() => setIsPickerOpen(true)}
										/>
										{!!formData.content && (
											<span className="truncate text-xs text-subtle">
												{contentUrl.split("/").pop()}
											</span>
										)}
									</div>
								)}

								{/* TTS mode: text input, voice selection, generate button */}
								{formData.contentType === FileTypes.Audio && contentInputMode === "tts" && (
									<div className="space-y-3">
										<TextArea
											className="min-h-[120px] !border !border-gray-200"
											handleChange={(e) => setTtsText(e.target.value)}
											label={ts("tts.textToConvert")}
											maxLength={5000}
											name="ttsText"
											placeholder={ts("tts.textPlaceholder")}
											showError={false}
											value={ttsText}
										/>

										<div>
											<label className="block text-[14px] font-bold text-primary-gray mb-1">
												{ts("tts.voice")}
											</label>
											<div className="flex flex-col items-center gap-2">
												<span className="text-sm text-gray-600 flex-1 truncate w-full">
													{selectedVoiceName || ts("tts.noVoiceSelected")}
												</span>
												<div className="w-full">
													<Button
														color={ButtonColor.ACTION_SECONDARY}
														size={ButtonSize.S}
														title={selectedVoiceId ? ts("tts.changeVoice") : ts("tts.selectVoice")}
														type="button"
														onClick={() => setShowVoiceModal(true)}
													/>
												</div>
											</div>
										</div>
										{ttsError && <p className="text-sm text-red-500">{ttsError}</p>}
										<Button
											className="mt-2"
											color={ButtonColor.BRAND}
											disabled={!ttsText.trim() || !selectedVoiceId || isGeneratingTts}
											isLoading={isGeneratingTts}
											size={ButtonSize.S}
											title={isGeneratingTts ? ts("tts.generating") : ts("tts.generateAudio")}
											onClick={handleGenerateTts}
										/>
									</div>
								)}

								{!!id && (
									<MediaPickerModal
										contentType={formData.contentType}
										courseId={+id}
										handleClose={() => setIsPickerOpen(false)}
										isOpened={isPickerOpen}
										onSelect={({ url }) => {
											handleSaveFormState(url, "content");

											setTimeout(() => {
												if (videoRef.current && formData.contentType === FileTypes.Video) {
													videoRef.current.load();
												}

												if (audioRef.current && formData.contentType === FileTypes.Audio) {
													audioRef.current.load();
												}
											}, 200);
										}}
									/>
								)}

								{formData.contentType === FileTypes.Image && contentInputMode === "ai" && id && (
									<AiImagePanel
										courseId={+id}
										phaseDataId={formData.id}
										onImageSelected={(fullFilePath) => handleSaveFormState(fullFilePath, "content")}
									/>
								)}

								{formData.contentType === FileTypes.Image && formData.content && (
									<img alt="Preview" className="mt-2 max-w-sm rounded shadow" src={contentUrl} />
								)}
								{formData.contentType === FileTypes.Audio &&
									formData?.content?.includes("http") && (
										<>
											<audio ref={audioRef} controls className="mt-2 w-full">
												<source src={contentUrl} />
												{ts("AudioNotSupported")}
											</audio>
											<div className="flex items-start">
												<AvatarButtons
													courseId={id !== undefined ? Number(id) : 0}
													label={"Avatar"}
													removeAvatarFile={removeAvatarFile}
													selectedAvatarId={selectedAvatarId}
													setSelectedAvatarId={setSelectedAvatarId}
													setUploadedAvatars={setUploadedAvatars}
													uploadedAvatars={uploadedAvatars}
													onChange={(value) => {
														if (!value || value === "0") {
															handleSaveFormState({ id: null, url: null }, "avatar");
															setSelectedAvatarId("");
															setSelectedAvatarUrl("");
														} else if (typeof value === "string") {
															handleSaveFormState({ id: value }, "avatar");
															setSelectedAvatarId(value);
															setSelectedAvatarUrl("");
														} else {
															handleSaveFormState({ id: value.id, url: value.url }, "avatar");
															setSelectedAvatarId(value.id);
															setSelectedAvatarUrl(value.url);
														}
													}}
												/>
											</div>

											<input
												accept="image/*"
												className="mb-2 block w-full text-sm text-slate-500
										file:mr-4 file:py-2 file:px-4
										file:rounded-full file:border-0
										file:text-sm file:font-semibold
										file:bg-blue-50 file:text-blue-700
										hover:file:bg-blue-100"
												type="file"
												onChange={async (e) => {
													const file = e.target.files?.[0];

													if (!file) {
														return;
													}

													try {
														const fileUrls = await uploadCourseFiles(
															[file],
															FileAssignment.Avatar,
															+id!
														);

														const url = fileUrls?.[0]?.fullFilePath;

														if (url?.startsWith("http")) {
															handleSaveFormState({ id: String(fileUrls?.[0]?.id), url }, "avatar");
															setSelectedAvatarId(String(fileUrls?.[0]?.id));
															setSelectedAvatarUrl(url);
															setUploadedAvatars((state) => [
																...state,
																{ url, id: String(fileUrls?.[0]?.id) },
															]);
														} else {
															alert("Upload fehlgeschlagen");
															e.target.value = "";
														}
													} catch (error) {
														alert("Upload fehlgeschlagen");
														console.error(error);
													}
												}}
											/>
										</>
									)}

								{!isUploadingFiles &&
									formData.contentType === FileTypes.Video &&
									formData.content && (
										<>
											{contentUrl.includes("youtube.com") || contentUrl.includes("youtu.be") ? (
												<iframe
													allowFullScreen
													allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
													className="mt-2 max-w-sm rounded shadow w-full aspect-video"
													frameBorder="0"
													src={contentUrl
														.replace("watch?v=", "embed/")
														.replace("youtu.be/", "youtube.com/embed/")}
													title="YouTube video"
												/>
											) : (
												<video
													ref={videoRef}
													controls
													className="mt-2 max-w-sm rounded shadow w-full aspect-video"
												>
													<source src={contentUrl} />
													Your browser does not support the video element.
												</video>
											)}
										</>
									)}
							</div>
						)}
					</div>
					<div className={classNames("flex flex-col gap-6 w-full items-start mt-2")}>
						{(formData.contentType === FileTypes.Video ||
							formData.contentType === FileTypes.Audio) && (
							<Checkbox
								isChecked={!!formData.autoplay}
								label={ts("autoplay")}
								name="autoplay"
								onChange={(checked) => handleSaveFormState(checked, "autoplay")}
							/>
						)}
					</div>
					<div className={classNames("flex flex-col gap-6 w-full items-start mt-2")}>
						{formData.contentType === FileTypes.Video && (
							<Checkbox
								isChecked={!!formData.large}
								label={ts("large")}
								name="large"
								onChange={(checked) => handleSaveFormState(checked, "large")}
							/>
						)}
					</div>
					<div className={classNames("flex flex-col gap-6 w-full items-start mt-2")}>
						<Checkbox
							isChecked={!!formData.effects?.includes(PhaseEffect.TV_GLITCH)}
							label={ts("effects.tvGlitch")}
							name="TV_GLITCH"
							onChange={() => {
								handleSaveFormState(
									formData.effects?.includes(PhaseEffect.TV_GLITCH)
										? formData.effects.filter((effect) => effect !== PhaseEffect.TV_GLITCH)
										: [...(formData.effects ?? []), PhaseEffect.TV_GLITCH],
									"effects"
								);
							}}
						/>
					</div>
					<div className={classNames("flex flex-col gap-6 w-full items-start mt-2")}>
						<Checkbox
							isChecked={!!formData.effects?.includes(PhaseEffect.DRONE)}
							label={ts("effects.drone")}
							name="DRONE"
							onChange={() => {
								handleSaveFormState(
									formData.effects?.includes(PhaseEffect.DRONE)
										? formData.effects.filter((effect) => effect !== PhaseEffect.DRONE)
										: [...(formData.effects ?? []), PhaseEffect.DRONE],
									"effects"
								);
							}}
						/>
					</div>
				</CollapsibleSection>
				{!currentNodeHasEdges && (
					<CollapsibleSection title={te("sectionEndConditions")}>
						<div className="w-full">
							<label
								className={classNames(
									"capitalize flex items-center text-[14px] font-bold text-primary-gray"
								)}
							>
								<span className="mr-2">{ts("phaseEndResult")}</span>
								<select
									className="border border-border rounded-btn px-2 py-1 text-sm font-normal bg-white text-text outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors"
									value={formData.phaseEndResult ?? PhaseEndResult.Success}
									onChange={(e) => {
										e.stopPropagation();
										handleSaveFormState(e.target.value as PhaseEndResult, "phaseEndResult");
									}}
								>
									<option value={PhaseEndResult.Success}>{ts("Success")}</option>
									<option value={PhaseEndResult.Failed}>{ts("Failed")}</option>
								</select>
							</label>
							<InputField
								containerClassName="w-full"
								error={
									formData.decisionName === undefined &&
									(formData.phaseEndText === undefined || formData.phaseEndText === "")
								}
								errorMessage={ts("mustBeSet.phaseEndText")}
								handleChange={(e) => {
									e.stopPropagation();
									handleSaveFormState(e.target.value, "phaseEndText");
								}}
								label={ts("phaseEndText")}
								name={"phaseEndText"}
								placeholder={ts("phaseEndTextExplain")}
								showError={
									formData.decisionName === undefined &&
									(formData.phaseEndText === undefined || formData.phaseEndText === "")
								}
								value={formData.phaseEndText || ""}
							/>
						</div>
					</CollapsibleSection>
				)}

				{/* Decision Options Section */}
				<CollapsibleSection
					badge={
						formData.decisionOptions
							? t("editor.decisionsCount", { count: formData.decisionOptions.length })
							: undefined
					}
					title={te("sectionDecisions")}
				>
					<div ref={decisionsSectionRef}>
						<div className="flex justify-between items-center mb-2">
							<Toggle
								isChecked={formData.decisionName !== undefined}
								onChange={() => {
									if (formData.decisionName === undefined) {
										handleAddDecisionSection();
										setShowDecisionModal(true);
									} else {
										handleRemoveDecisionSection();
										setShowDecisionModal(false);
									}
								}}
							/>
						</div>

						{formData.decisionName !== undefined && (
							<>
								<button
									className="bg-ink text-white px-3 py-1.5 rounded-btn hover:bg-neutral-700 transition-colors text-sm"
									type="button"
									onClick={() => setShowDecisionModal(true)}
								>
									{ts("editDecision")}
								</button>
								{formData.decisionOptions?.find((option) => option.option === "") && (
									<p className="text-red-500 text-sm"> {ts("mustBeSet.decisionOption")} </p>
								)}
								{formData.decisionName === "" && (
									<p className="text-red-500 text-sm"> {ts("mustBeSet.decisionName")} </p>
								)}
								{formData.timeLimit && !formData.timeLeftDecisionId && (
									<p className="text-red-500 text-sm"> {ts("mustBeSet.timeLeftDecision")} </p>
								)}
								{!!formData.timeLeftDecisionId && !formData.timeLimit && (
									<p className="text-red-500 text-sm">{ts("mustBeSet.timeLimit")}</p>
								)}
								{formData.confirmationRequired === DecisionConfirmationTypes.NONE &&
									formData.decisionOptions &&
									formData.decisionOptions.length > 1 && (
										<p className="text-red-500 text-sm">{ts("mustBeSet.confirmationRequired")}</p>
									)}
							</>
						)}
					</div>
				</CollapsibleSection>

				{/* Spoiler Content Section */}
				<CollapsibleSection
					badge={formData.spoilerContent ? "✓" : undefined}
					title={te("sectionSpoiler")}
				>
					<div ref={spoilerSectionRef}>
						<div className="flex justify-between items-center mb-2">
							<Toggle
								isChecked={formData.spoilerContent !== undefined}
								onChange={() => {
									if (formData.spoilerContent === undefined) {
										addSpoilerContent();
										setShowSpoilerModal(true);
									} else {
										removeSpoilerContent();
										setShowSpoilerModal(false);
									}
								}}
							/>
						</div>

						{formData.spoilerContent !== undefined && (
							<button
								className="bg-ink text-white px-3 py-1.5 rounded-btn hover:bg-neutral-700 transition-colors text-sm"
								type="button"
								onClick={() => setShowSpoilerModal(true)}
							>
								{ts("editSpoiler")}
							</button>
						)}
					</div>
				</CollapsibleSection>

				{/* Time-Delayed Content Section */}
				<CollapsibleSection
					badge={
						formData.timeDelayedContent?.length
							? t("editor.delayedItemsCount", { count: formData.timeDelayedContent.length })
							: undefined
					}
					title={te("sectionTimeDelayed")}
				>
					<div>
						<div className="flex justify-between items-center mb-3">
							<Toggle
								isChecked={
									formData.timeDelayedContent !== undefined &&
									formData.timeDelayedContent.length > 0
								}
								onChange={() => {
									if (
										formData.timeDelayedContent === undefined ||
										formData.timeDelayedContent.length === 0
									) {
										addTimeDelayedContentItem();
										setShowTimeDelayedModal(true);
									} else {
										handleSaveFormState(undefined, "timeDelayedContent");
										setShowTimeDelayedModal(false);
									}
								}}
							/>
						</div>

						{formData.timeDelayedContent !== undefined &&
							formData.timeDelayedContent.length > 0 && (
								<button
									className="bg-ink text-white px-3 py-1.5 rounded-btn hover:bg-neutral-700 transition-colors text-sm"
									type="button"
									onClick={() => setShowTimeDelayedModal(true)}
								>
									{ts("editTimeDelayedContent")}
								</button>
							)}
					</div>
				</CollapsibleSection>
			</div>
			<div className="flex justify-between items-center mb-3">
				<button
					className="bg-red-500 text-white px-3 py-1.5 rounded hover:bg-red-600 transition-colors text-sm"
					type="button"
					onClick={() => setShowDeleteConfirmModal(true)}
				>
					{t("basics.delete")}
				</button>
			</div>
			{showDecisionModal && (
				<Modal
					cancelButtonText={t("basics.close")}
					handleClose={() => setShowDecisionModal(false)}
					isOpened={showDecisionModal}
					size={"lg"}
					title={ts("editDecision")}
				>
					<>
						<InputField
							containerClassName="w-full"
							error={formData.decisionName === "" ? ts("decisionName") : undefined}
							handleChange={(e) => {
								e.stopPropagation();
								handleSaveFormState(e.target.value, "decisionName");
							}}
							label={ts("decisionTitle")}
							name={"decisionName"}
							placeholder={ts("decisionTitle")}
							showError={formData.decisionName === ""}
							value={formData.decisionName || ""}
						/>
						<div className="flex flex-col items-end gap-4 w-full mt-3">
							{/* Item 1: Time Limit Input */}
							<div className="w-full">
								<InputField
									className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
									containerClassName="w-full"
									error={
										!formData.timeLimit && !!formData.timeLeftDecisionId
											? ts("timeLimit")
											: undefined
									}
									handleChange={(e) => {
										e.stopPropagation();
										const numericValue = e.target.value.replace(/[^0-9]/g, "");

										handleSaveFormState(
											e.target.value === "" ? undefined : parseFloat(numericValue),
											"timeLimit"
										);
									}}
									label={ts("timeLimit") + " " + ts("inSeconds")}
									name={"timeLimit"}
									placeholder={"600"}
									showError={!formData.timeLimit && !!formData.timeLeftDecisionId}
									type="number"
									value={formData.timeLimit?.toString() ?? ""}
								/>
							</div>

							{/* Item 2: Timeout Action Dropdown */}
							<div className="w-full">
								<Dropdown
									floating
									data={[
										{
											title: "None",
											id: null,
										},
										// The option's own words. The database key used to be appended —
										// "Die Mitarbeitenden sollten … - 6" — which is the phase it leads
										// to, spelled as a number nobody outside the database can read.
										...(formData.decisionOptions?.map((option) => ({
											title: option.option,
											id: option.phaseId,
										})) ?? []),
									]}
									defaultValue={
										formData.decisionOptions?.find(
											(option) => option.phaseId === formData.timeLeftDecisionId
										)
											? {
													title:
														formData.decisionOptions?.find(
															(option) => option.phaseId === formData.timeLeftDecisionId
														)?.option +
														" - " +
														formData.timeLeftDecisionId,
													id: formData.timeLeftDecisionId?.toString() ?? "",
												}
											: {
													title: "None",
													id: null,
												}
									}
									error={!formData.timeLeftDecisionId && !!formData.timeLimit}
									handleSelect={(value) => handleSaveFormState(value?.id, "timeLeftDecisionId")}
									label={ts("TimeoutPhaseID")}
								/>
							</div>

							{/* Item 3: Confirmation Dropdown */}
							<div className="w-full">
								<Dropdown
									floating
									data={confirmationRequiredOptions}
									defaultValue={
										confirmationRequiredOptions.find(
											(option) => option.id === formData.confirmationRequired
										) ?? confirmationRequiredOptions[0]
									}
									error={
										(!formData.confirmationRequired ||
											formData.confirmationRequired === DecisionConfirmationTypes.NONE) &&
										formData.decisionOptions &&
										formData.decisionOptions.length > 1
									}
									handleSelect={(value) => handleSaveFormState(value?.id, "confirmationRequired")}
									label={ts("confirmation")}
								/>
							</div>
						</div>
						<h4 className="font-semibold text-sm mt-4 mb-2">{ts("decisionOptions")} </h4>
						<div className="space-y-2">
							{formData.decisionOptions?.map((option: DecisionOption, index: number) => (
								<div key={index} className="flex items-center gap-2  justify-between">
									<InputField
										containerClassName="flex-grow"
										error={option.option === "" ? ts("option") : undefined}
										handleChange={(e) => updateDecisionOption(index, "option", e.target.value)}
										label={ts("option") + " #" + (index + 1)}
										name={`option_text_${index}`}
										placeholder={ts("option") + " #" + (index + 1)}
										showError={option.option === ""}
										type="text"
										value={option.option}
									/>
									<div className="w-1/2">
										<Dropdown
											floating
											data={[
												{
													title: "None",
													id: null,
												},
												...(nextNodes?.map((n) => ({
													title: n?.data?.phaseName?.toString() ?? "Untitled Phase",
													id: n.id,
												})) ?? []),
											]}
											defaultValue={
												nextNodes?.find((n) => n.id === option.phaseId?.toString())
													? {
															title:
																nextNodes
																	?.find((n) => n.id === option.phaseId?.toString())
																	?.data.phaseName?.toString() ?? "",
															id:
																nextNodes?.find((n) => n.id === option.phaseId?.toString())?.id ??
																null,
														}
													: {
															title: "None",
															id: null,
														}
											}
											error={formData.timeLeftDecisionId === undefined && !!formData.timeLimit}
											handleSelect={(value) => {
												updateDecisionOption(index, "phaseId", value?.id ?? null);
											}}
											label={ts("leadsToPhase")}
										/>
									</div>
									<div
										className="w-fit cursor-pointer p-2 bg-white border border-gray-200 rounded-lg"
										onClick={() => removeDecisionOption(index)}
									>
										<Icon icon={faTrash} />
									</div>
								</div>
							))}
						</div>
						{formData.decisionOptions && (
							<div className="mt-3 flex items-center gap-2 justify-end">
								<div className="flex mr-auto gap-2">
									<h3 className="font-semibold text-sm">{ts("explanation")}</h3>
									<Toggle
										isChecked={formData.requireExplanation || false}
										onChange={() => {
											handleSaveFormState(!formData.requireExplanation, "requireExplanation");
										}}
									/>
								</div>
								{/* Four options is the maximum; only adding stops there, the explanation
								    setting stays reachable (BCM360-366). */}
								{formData.decisionOptions.length < 4 && (
									<>
										<div className="w-fit">
											<Button
												color={ButtonColor.ACTION_SECONDARY}
												size={ButtonSize.M}
												title={`+ ${ts("option")}`}
												type="button"
												onClick={addDecisionOptionOnly}
											/>
										</div>
										<div className="w-fit">
											<Button
												color={ButtonColor.ACTION}
												size={ButtonSize.M}
												title={`+ ${ts("option")} & ${ts("newPhase")}`}
												type="button"
												onClick={addDecisionOptionAndPhase}
											/>
										</div>
									</>
								)}
							</div>
						)}
					</>
				</Modal>
			)}

			{showSpoilerModal && (
				<Modal
					cancelButtonText={t("basics.close")}
					handleClose={() => setShowSpoilerModal(false)}
					isOpened={showSpoilerModal}
					title="Edit Spoiler"
				>
					<div className="space-y-3 mt-2 p-3 border rounded bg-gray-50 shadow-sm">
						{" "}
						{/* Added shadow-sm for consistency */}
						<InputField
							containerClassName="w-full"
							handleChange={(e) => {
								e.stopPropagation();
								handleSpoilerContentChange("title", e.target.value);
							}}
							label={ts("title2")}
							name={"spoilerContentTitle"}
							placeholder={ts("newTitle")}
							value={formData.spoilerContent?.title || ""}
						/>
						<InputField
							className="bg-white h-24 resize-none"
							containerClassName="custom-scrollbar"
							handleChange={(e) => {
								e.stopPropagation();
								handleSpoilerContentChange("content", e.target.value);
							}}
							label={ts("content")}
							multiline={true}
							name={"spoilerContentMain"}
							placeholder={ts("newContent")}
							showError={false}
							value={formData?.spoilerContent?.content || ""}
						/>
					</div>
				</Modal>
			)}

			{showTimeDelayedModal && (
				<Modal
					cancelButtonText={t("basics.close")}
					handleClose={() => setShowTimeDelayedModal(false)}
					handleSecondSave={() => {
						addTimeDelayedContentItem();
					}}
					isOpened={showTimeDelayedModal}
					secondSubmitButtonText={ts("add")}
					size={"lg"}
					title="Edit Time Delayed Content"
				>
					<div className="space-y-3 custom-scrollbar max-h-[70vh] overflow-y-auto">
						{(formData.timeDelayedContent || []).map((item, index) => (
							<div key={index} className="space-y-3">
								<div ref={timeDelayedSectionRef} className="flex justify-between items-center">
									<h4 className="font-medium text-sm text-gray-700">
										{ts("timeDelayedContent")} #{index + 1}
									</h4>
									<button
										className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 transition-colors text-xs"
										type="button"
										onClick={() => removeTimeDelayedContentItem(index)}
									>
										- {ts("remove")}
									</button>
								</div>
								<InputField
									containerClassName="w-full"
									handleChange={(e) =>
										handleTimeDelayedContentChange(index, "title", e.target.value)
									}
									label={ts("title2")}
									name={`delayed_title_${index}`}
									placeholder={ts("newTitle")}
									value={item.title}
								/>
								<div className="flex flex-col gap-2 w-full items-start">
									<Dropdown
										floating
										data={fileTypesOptions}
										defaultValue={
											fileTypesOptions.find((option) => option.id === item.contentType) ??
											fileTypesOptions[0]
										}
										handleSelect={(value) =>
											handleTimeDelayedContentChange(
												index,
												"contentType",
												value?.id as unknown as FileTypes
											)
										}
										label={ts("contentType")}
									/>
								</div>
								<div>
									{item.contentType !== FileTypes.Text && (
										<input // File input only for non-text types
											accept={
												item.contentType === FileTypes.Image
													? "image/*"
													: item.contentType === FileTypes.Video
														? "video/*"
														: item.contentType === FileTypes.Audio
															? "audio/*"
															: undefined
											}
											className="mb-2 block w-full text-sm text-slate-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-full file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
											type="file"
											onChange={async (e) => {
												const file = e.target.files?.[0];

												if (!file) {
													return;
												}

												try {
													const fileUrls = await uploadCourseFiles(
														[file],
														FileAssignment.Content,
														+id!
													);

													if (fileUrls?.[0]?.fullFilePath) {
														handleTimeDelayedContentChange(
															index,
															"content",
															fileUrls[0].fullFilePath
														);

														setTimeout(() => {
															if (videoDelayedContentRef.current) {
																videoDelayedContentRef.current.load();
															}

															if (audioDelayedContentRef.current) {
																audioDelayedContentRef.current.load();
															}
														}, 200);
													} else {
														alert("Upload fehlgeschlagen");
													}
												} catch (error) {
													alert("Upload fehlgeschlagen");
													console.error(error);
												}
											}}
										/>
									)}

									{item.contentType === FileTypes.Image && item.content && (
										<img
											alt="Preview"
											className="mt-2 max-w-sm rounded shadow"
											src={phaseMediaUrl(item.content, item.contentType)}
										/>
									)}
									{item.contentType === FileTypes.Audio && item.content?.includes("http") && (
										<audio ref={audioDelayedContentRef} controls className="mt-2 w-full">
											<source src={phaseMediaUrl(item.content, item.contentType)} />
											Your browser does not support the audio element.
										</audio>
									)}
									{item.contentType === FileTypes.Video &&
										item.content &&
										item.content.length > 0 && (
											<video
												ref={videoDelayedContentRef}
												controls
												className="mt-2 max-w-sm rounded shadow w-full aspect-video"
											>
												<source src={phaseMediaUrl(item.content, item.contentType)} />
												Your browser does not support the video element.
											</video>
										)}
								</div>
								<div className="flex flex-col gap-2 w-full items-start">
									<Checkbox
										isChecked={!!item.autoplay}
										label={ts("autoplay")}
										name={`timeDelayedAutoplay-${index}`}
										onChange={() => {
											const currentItem = formData.timeDelayedContent?.[index];

											if (currentItem) {
												handleTimeDelayedContentChange(index, "autoplay", !currentItem.autoplay);
											}
										}}
									/>
								</div>
								{item.contentType === FileTypes.Video && (
									<div className="flex flex-col gap-2 w-full items-start">
										<Checkbox
											isChecked={!!item.large}
											label={ts("large")}
											name={`timeDelayedLarge-${index}`}
											onChange={() => {
												const currentItem = formData.timeDelayedContent?.[index];

												if (currentItem) {
													handleTimeDelayedContentChange(index, "large", !currentItem.large);
												}
											}}
										/>
									</div>
								)}
								{item.contentType === FileTypes.Text && ( // Text area only for Text type
									<InputField
										handleChange={(e) =>
											handleTimeDelayedContentChange(index, "content", e.target.value)
										}
										label={ts("content")}
										multiline={true}
										name={`delayed_content_${index}`}
										placeholder={ts("newContent")}
										showError={false}
										value={item.content}
									/>
								)}
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
									<InputField
										className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
										handleChange={(e) => {
											handleTimeDelayedContentChange(index, "startTimeInSeconds", e.target.value);
										}}
										label={ts("startTime") + " " + ts("inSeconds")}
										name={`delayed_startTime_${index}`}
										placeholder={"0"}
										type="number"
										value={item.startTimeInSeconds?.toString() || ""}
									/>
									<InputField
										className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
										handleChange={(e) => {
											handleTimeDelayedContentChange(index, "endTimeInSeconds", e.target.value);
										}}
										label={ts("endTime") + " " + ts("inSeconds")}
										name={`delayed_endTime_${index}`}
										placeholder={"20"}
										type="number"
										value={item.endTimeInSeconds?.toString() || ""}
									/>
								</div>
							</div>
						))}
					</div>
				</Modal>
			)}

			{showVoiceModal && (
				<VoiceSelectModal
					courseLanguage={courseLanguage}
					isOpen={showVoiceModal}
					selectedVoiceId={selectedVoiceId}
					onClose={() => setShowVoiceModal(false)}
					onConfirm={(voiceId, voiceName) => {
						setSelectedVoiceId(voiceId);
						setSelectedVoiceName(voiceName);
						setShowVoiceModal(false);
					}}
				/>
			)}
			{showDeleteConfirmModal && (
				<Modal
					handleClose={() => setShowDeleteConfirmModal(false)}
					handleSave={() => {
						handleDeletePhase();
						setShowDeleteConfirmModal(false);
					}}
					isOpened={showDeleteConfirmModal}
					submitButtonColor="error"
					submitButtonText={t("basics.delete")}
					title={t("editor.deletePhaseTitle")}
				>
					<p>
						{/* An unnamed phase used to fall back to the field's own placeholder, so
						    this asked to delete phase "Phase name" — quoting UI chrome back at the
						    author as though they had typed it. */}
						{formData.phaseName
							? t("editor.deletePhaseConfirmation", { phaseName: formData.phaseName })
							: t("editor.deletePhaseConfirmationUnnamed")}
					</p>
				</Modal>
			)}
		</div>
	);
}
